/**
 * ATL-1323 — turning a support agent's drafted product into the product screen's create form.
 *
 * Pure (no React Native, no Firestore) so the mapping is testable on its own. The screen keeps its
 * own create path exactly as it is: `getFormValues(null, …)` builds the defaults a manual create
 * starts from, and these overrides are laid on top.
 *
 * What a draft may prefill is deliberately narrow, and matches what the backend contract accepts:
 * the name, one suggested sale price, the unit (by name or short name) and category BY NAME from the
 * organization's own dictionaries, and the weight/service flags. Stock stays the screen's `'0'` and the cost price stays
 * empty, exactly as for a manual create: the customer enters them, and `validateProduct` refuses a
 * save without a cost price. Nothing here can make a draft savable by pressing Save alone.
 */
import type { ProductDraftItem } from './types'

interface Named {
  name?: unknown
}

interface NamedUnit extends Named {
  shortName?: unknown
}

interface StoreLike {
  id?: string
  firestoreId?: string
}

export interface DraftFormDeps<Unit extends NamedUnit, Category extends Named> {
  productUnitsDict: Record<string, Unit>
  productCategoriesDict: Record<string, Category>
  /** The stores this user sees — the only stores the screen renders a price input for. */
  visibleStoresForUser: Record<string, StoreLike>
}

export interface DraftFormOverrides<Unit, Category> {
  name: string
  salePrices: Record<string, string>
  /** Present only when the draft's unit matched exactly one of the organization's units. */
  productUnit?: Unit
  /** The matched category, or null so the customer picks one (the field is required). */
  categoryID: Category | null
  type: boolean
  isService: boolean
}

/** Case- and whitespace-insensitive, in the locale the product names are written in. */
export const normalizeProductName = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru-RU') : ''

/**
 * The one dictionary entry whose name matches. Two entries with the same name are ambiguous, and
 * picking either would be a guess the customer cannot see, so neither is returned.
 */
export const matchByName = <T extends Named>(dict: Record<string, T>, name: string | null): T | null => {
  const matches = matchesOf(dict, name, (entry) => entry?.name)
  return matches.length === 1 ? matches[0] : null
}

/**
 * The unit a drafted unit hint selects: the one unit whose full name matches, else the one unit whose
 * short name matches. Every organization is seeded with full names (`Штука`, `Килограмм`, `Литр`) and
 * their short forms (`шт`, `кг`, `л`) in `shortName`, and the agent writes the short form, so matching
 * `name` alone (ATL-1610) left every drafted unit unmatched in a real organization. The full name is
 * tried first because it is the unit's identity: an organization that added its own unit NAMED `шт`
 * beside the seeded `Штука`/`шт` keeps getting its own unit rather than an ambiguity. Two units
 * matching within one tier stay ambiguous, exactly as for a category.
 */
export const matchUnitByName = <T extends NamedUnit>(dict: Record<string, T>, name: string | null): T | null => {
  const byName = matchesOf(dict, name, (entry) => entry?.name)
  // An ambiguous full name stops here: falling through to the short names would pick a unit the
  // customer cannot see was chosen over the two that carry the exact name.
  if (byName.length > 0) return byName.length === 1 ? byName[0] : null
  const byShortName = matchesOf(dict, name, (entry) => entry?.shortName)
  return byShortName.length === 1 ? byShortName[0] : null
}

/**
 * The unit to print beside a drafted price on the card: the short name of the unit the product form
 * will select (`matchUnitByName`), or null when the hint matches none, so the card never names a unit
 * the saved product would not carry.
 */
export const draftPriceUnitLabel = <T extends NamedUnit>(dict: Record<string, T>, name: string | null): string | null => {
  const unit = matchUnitByName(dict, name)
  if (!unit) return null
  for (const candidate of [unit.shortName, unit.name]) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  return null
}

const matchesOf = <T>(dict: Record<string, T>, name: string | null, nameOf: (entry: T) => unknown): T[] => {
  const wanted = normalizeProductName(name)
  if (!wanted) return []
  return Object.values(dict ?? {}).filter((entry) => normalizeProductName(nameOf(entry)) === wanted)
}

/**
 * The create-form values a draft replaces. `baseSalePrices` is the screen's own default map (every
 * store, empty), so stores this user cannot see keep exactly what a manual create would give them.
 */
export const draftFormOverrides = <Unit extends NamedUnit, Category extends Named>(
  item: ProductDraftItem,
  baseSalePrices: Record<string, string>,
  deps: DraftFormDeps<Unit, Category>
): DraftFormOverrides<Unit, Category> => {
  const salePrices: Record<string, string> = { ...baseSalePrices }
  if (item.salePrice !== null && Number.isFinite(item.salePrice)) {
    for (const store of Object.values(deps.visibleStoresForUser ?? {})) {
      const key = store?.firestoreId || store?.id
      if (key) salePrices[key] = String(item.salePrice)
    }
  }
  const unit = matchUnitByName(deps.productUnitsDict, item.unitName)
  return {
    name: item.name,
    salePrices,
    ...(unit ? { productUnit: unit } : {}),
    categoryID: matchByName(deps.productCategoriesDict, item.categoryName),
    type: item.soldByWeight === true,
    isService: item.isService === true,
  }
}

/**
 * The normalized names of every product in the catalogue. The card checks each drafted name against
 * it, so a second save of the same product — say, after a report-back that did not reach the
 * backend — is visible before it happens rather than after.
 *
 * Built once per catalogue change rather than scanned per drafted item: `productsDict` changes on
 * every stock update, and a 20-item card over a large catalogue would otherwise re-normalize every
 * product name 20 times on each of those renders.
 */
export const catalogueProductNames = <P extends Named>(productsDict: Record<string, P>): Set<string> => {
  const names = new Set<string>()
  for (const product of Object.values(productsDict ?? {})) {
    const name = normalizeProductName(product?.name)
    if (name) names.add(name)
  }
  return names
}

/** Whether a product already in the catalogue carries the drafted name (see `catalogueProductNames`). */
export const isNameInCatalogue = (catalogueNames: ReadonlySet<string>, name: string): boolean => {
  const wanted = normalizeProductName(name)
  return wanted !== '' && catalogueNames.has(wanted)
}

/**
 * The app's own rule for who may create products (`HomeScreen.js`, `EditCartItemScreen.js`), which
 * the backend enforces again on every proposal route.
 */
export const canCreateProducts = (
  userRole: { id?: string; permissions?: Record<string, unknown> } | null | undefined,
  user: { role?: string } | null | undefined
): boolean =>
  Boolean(userRole?.permissions?.['Products'] === true || userRole?.id === 'admin' || user?.role === 'admin')
